// Versão: 3.06
// Data..: 2026-09-25
// Abertura de chamado com anexos, categoria, impacto/urgência, catálogo e sugestão de KB.
// v3.06: staff pode criar categoria na hora quando a desejada não existe (campo sempre visível).
// v3.03: staff pode abrir chamado em nome de um cliente (cria o cliente na base, empresa obrigatória).
// v3.04: permite cadastrar uma nova empresa no próprio fluxo quando ela não existe na lista.
// v3.05: autocomplete de cliente existente (busca por nome/e-mail preenche e-mail, nome e empresa).
// v3.06: checklist inicial (staff) — categorias com subitens criados junto com o chamado.

import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  TicketPriority,
  Impact,
  Urgency,
  UserRole,
  calcPriority,
  PriorityLabels,
  ImpactLabels,
  UrgencyLabels,
  type CreateTicketInput,
  type Department,
  type Category,
  type Company,
  type ServiceCatalogItem,
  type Ticket,
  type Attachment,
  type Paginated,
} from '@makweb/shared';
import { api } from '../lib/api.js';
import { uploadFile } from '../lib/upload.js';
import { useAuth } from '../context/AuthContext.js';
import { useToast } from '../context/ToastContext.js';
import { RichEditor } from '../components/RichEditor.js';
import { buildCategoryOptions } from '../lib/categories.js';

export function NewTicketPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const { user } = useAuth();
  const isStaff = user?.role === UserRole.ADMIN || user?.role === UserRole.AGENT;
  const fileRef = useRef<HTMLInputElement>(null);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [catalog, setCatalog] = useState<ServiceCatalogItem[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [form, setForm] = useState<CreateTicketInput>({ subject: '', body: '', priority: TicketPriority.NORMAL });
  const [scheduled, setScheduled] = useState('');
  const [isTask, setIsTask] = useState(false);
  // Checklist inicial (staff): categorias (pais) com subitens.
  const [checklist, setChecklist] = useState<{ text: string; children: string[] }[]>([]);
  const [newGroup, setNewGroup] = useState('');
  const [newChild, setNewChild] = useState<Record<number, string>>({});
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [saving, setSaving] = useState(false);
  // Abrir em nome de um cliente (staff).
  const [onBehalf, setOnBehalf] = useState(false);
  const [client, setClient] = useState({ email: '', name: '', companyId: '' });
  // Base de clientes para autocomplete (carregada ao ativar "em nome de").
  const [clientBase, setClientBase] = useState<{ id: string; name: string; email: string; companyId?: string }[]>([]);
  const [clientQuery, setClientQuery] = useState(''); // termo digitado (nome ou e-mail)
  const [showSuggest, setShowSuggest] = useState(false);
  // Cadastro de nova empresa no próprio fluxo (quando não existe na lista).
  const [newCompanyName, setNewCompanyName] = useState('');
  const NEW_COMPANY = '__new__';
  // Criação de categoria no próprio fluxo (staff): quando a categoria desejada não existe.
  const [newCategoryName, setNewCategoryName] = useState('');
  const NEW_CATEGORY = '__new_cat__';

  useEffect(() => {
    api.get<Paginated<Department>>('/departments').then((r) => setDepartments(r.items)).catch(() => setDepartments([]));
    // Categorias só existem para a equipe; clientes recebem lista vazia (silencioso).
    api.get<Paginated<Category>>('/categories').then((r) => setCategories(r.items)).catch(() => setCategories([]));
    // Catálogo de serviços (qualquer autenticado).
    api.get<{ items: ServiceCatalogItem[] }>('/catalog').then((r) => setCatalog(r.items)).catch(() => setCatalog([]));
  }, []);

  // Empresas para o seletor "em nome de" (apenas staff).
  useEffect(() => {
    if (!isStaff) return;
    api.get<Paginated<Company>>('/companies').then((r) => setCompanies(r.items)).catch(() => setCompanies([]));
  }, [isStaff]);

  // Base de clientes para o autocomplete (carrega ao ativar "em nome de cliente").
  useEffect(() => {
    if (!onBehalf || clientBase.length) return;
    api.get<Paginated<{ id: string; name: string; email: string; role: string; companyId?: string }>>('/users')
      .then((r) => setClientBase(r.items.filter((u) => u.role === 'client').map(({ id, name, email, companyId }) => ({ id, name, email, companyId }))))
      .catch(() => {});
  }, [onBehalf, clientBase.length]);

  // Sugestões filtradas pelo termo digitado (nome ou e-mail), no máximo 8.
  const clientSuggestions = (() => {
    const q = clientQuery.trim().toLowerCase();
    if (q.length < 2) return [];
    return clientBase.filter((c) => `${c.name} ${c.email}`.toLowerCase().includes(q)).slice(0, 8);
  })();

  // Preenche e-mail, nome e empresa ao escolher um cliente da lista.
  const pickClient = (c: { name: string; email: string; companyId?: string }) => {
    setClient({ email: c.email, name: c.name, companyId: c.companyId ?? '' });
    setClientQuery(c.name || c.email);
    setShowSuggest(false);
  };

  // Aplica um item de catálogo pré-preenchendo assunto/categoria/prioridade/impacto/urgência.
  const applyCatalogItem = (itemId: string) => {
    const item = catalog.find((c) => c.id === itemId);
    if (!item) return;
    setForm((f) => ({
      ...f,
      subject: f.subject || item.name,
      body: f.body || (item.description ? `<p>${item.description}</p>` : f.body),
      departmentId: item.departmentId ?? f.departmentId,
      categoryId: item.categoryId ?? f.categoryId,
      priority: item.defaultPriority ?? f.priority,
      impact: item.defaultImpact ?? f.impact,
      urgency: item.defaultUrgency ?? f.urgency,
    }));
  };

  const categoryOptions = buildCategoryOptions(categories);
  // Quando impacto e urgência estão definidos, a prioridade é calculada pela matriz.
  const matrixPriority =
    form.impact && form.urgency ? calcPriority(form.impact, form.urgency) : undefined;

  // Sugestão de artigos da KB conforme o assunto digitado (debounce simples).
  const [suggestions, setSuggestions] = useState<{ id: string; title: string; slug: string }[]>([]);
  useEffect(() => {
    const q = form.subject.trim();
    if (q.length < 4) { setSuggestions([]); return; }
    const t = setTimeout(() => {
      api.get<{ items: { id: string; title: string; slug: string }[] }>(`/kb/suggest?q=${encodeURIComponent(q)}`)
        .then((r) => setSuggestions(r.items))
        .catch(() => setSuggestions([]));
    }, 350);
    return () => clearTimeout(t);
  }, [form.subject]);

  const onPickFiles = async (files: FileList | null) => {
    if (!files?.length) return;
    try {
      const up = await Promise.all(Array.from(files).map(uploadFile));
      setAttachments((a) => [...a, ...up]);
      toast.success(`${up.length} anexo(s) adicionado(s)`);
    } catch {
      toast.error('Falha ao enviar anexo');
    }
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    // Validação do bloco "em nome de cliente": e-mail e empresa são obrigatórios.
    const creatingCompany = onBehalf && client.companyId === NEW_COMPANY;
    const creatingCategory = form.categoryId === NEW_CATEGORY;
    if (onBehalf) {
      if (!client.email.trim()) { toast.error('Informe o e-mail do cliente'); return; }
      if (!client.companyId) { toast.error('Selecione a empresa do cliente'); return; }
      if (creatingCompany && !newCompanyName.trim()) { toast.error('Informe o nome da nova empresa'); return; }
    }
    if (creatingCategory && !newCategoryName.trim()) { toast.error('Informe o nome da nova categoria'); return; }
    setSaving(true);
    try {
      // Se o atendente optou por cadastrar uma empresa nova, cria antes de abrir o chamado.
      let companyId = client.companyId;
      if (creatingCompany) {
        const company = await api.post<Company>('/companies', { name: newCompanyName.trim() });
        companyId = company.id;
        // Reflete a nova empresa na lista e seleciona-a.
        setCompanies((list) => [...list, company]);
        setClient((c) => ({ ...c, companyId: company.id }));
        setNewCompanyName('');
      }

      // Se optou por criar categoria nova, cria antes de abrir o chamado.
      let categoryId = form.categoryId;
      if (creatingCategory) {
        const category = await api.post<Category>('/categories', { name: newCategoryName.trim() });
        categoryId = category.id;
        setCategories((list) => [...list, category]);
        setForm((f) => ({ ...f, categoryId: category.id }));
        setNewCategoryName('');
      }

      const payload: CreateTicketInput = {
        ...form,
        categoryId,
        scheduledAt: scheduled ? new Date(scheduled).toISOString() : undefined,
        attachments: attachments.length ? attachments : undefined,
        isTask,
        checklist: (() => {
          const gs = checklist
            .filter((g) => g.text.trim())
            .map((g) => ({ text: g.text.trim(), children: g.children.filter((c) => c.trim()) }));
          return gs.length ? gs : undefined;
        })(),
        ...(onBehalf
          ? {
              requesterEmail: client.email.trim(),
              requesterFullName: client.name.trim() || undefined,
              requesterCompanyId: companyId,
            }
          : {}),
      };
      const ticket = await api.post<Ticket>('/tickets', payload);
      toast.success('Chamado aberto');
      navigate(`/tickets/${ticket.id}`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Falha ao criar chamado');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ maxWidth: 760, margin: '0 auto' }}>
      <Link to="/tickets" className="text-sm">← Voltar</Link>
      <h1 className="mt-2">Novo chamado</h1>
      <form onSubmit={submit} className="card">
        {isStaff && (
          <div className="field" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', padding: 'var(--s4)' }}>
            <label className="flex items-center gap-2" style={{ cursor: 'pointer', marginBottom: onBehalf ? 'var(--s3)' : 0 }}>
              <input type="checkbox" checked={onBehalf} onChange={(e) => setOnBehalf(e.target.checked)} />
              <span className="font-semibold">Abrir em nome de um cliente</span>
            </label>
            {onBehalf && (
              <>
                {/* Busca de cliente existente: digite nome ou e-mail e selecione. */}
                <div className="field mb-0" style={{ position: 'relative' }}>
                  <label className="label">Buscar cliente (nome ou e-mail)</label>
                  <input
                    className="input"
                    value={clientQuery}
                    onChange={(e) => { setClientQuery(e.target.value); setShowSuggest(true); }}
                    onFocus={() => setShowSuggest(true)}
                    onBlur={() => setTimeout(() => setShowSuggest(false), 150)}
                    placeholder="Comece a digitar para buscar na base..."
                  />
                  {showSuggest && clientSuggestions.length > 0 && (
                    <div className="card card-flush" style={{ position: 'absolute', zIndex: 20, left: 0, right: 0, marginTop: 4, maxHeight: 220, overflowY: 'auto', boxShadow: 'var(--shadow-lg)' }}>
                      {clientSuggestions.map((c) => (
                        <button
                          key={c.id}
                          type="button"
                          onMouseDown={(e) => { e.preventDefault(); pickClient(c); }}
                          style={{ display: 'block', width: '100%', textAlign: 'left', padding: '9px 12px', border: 'none', borderBottom: '1px solid var(--border)', background: 'transparent', cursor: 'pointer' }}
                        >
                          <span className="font-semibold text-sm">{c.name}</span>
                          <span className="text-dim text-xs"> · {c.email}</span>
                        </button>
                      ))}
                    </div>
                  )}
                  <p className="text-dim text-xs mt-1">Selecione um cliente para preencher e-mail, nome e empresa. Ou preencha manualmente abaixo para criar um novo.</p>
                </div>
                <div className="flex gap-4 flex-wrap mt-3">
                  <div className="field flex-1 mb-0" style={{ minWidth: 220 }}>
                    <label className="label">E-mail do cliente *</label>
                    <input className="input" type="email" value={client.email} onChange={(e) => setClient({ ...client, email: e.target.value })} placeholder="cliente@empresa.com.br" required />
                  </div>
                  <div className="field flex-1 mb-0" style={{ minWidth: 220 }}>
                    <label className="label">Nome do cliente</label>
                    <input className="input" value={client.name} onChange={(e) => setClient({ ...client, name: e.target.value })} placeholder="Nome completo" />
                  </div>
                </div>
                <div className="field mb-0 mt-3">
                  <label className="label">Empresa *</label>
                  <select className="select" value={client.companyId} onChange={(e) => setClient({ ...client, companyId: e.target.value })} required>
                    <option value="">Selecione a empresa</option>
                    {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                    <option value={NEW_COMPANY}>➕ Cadastrar nova empresa...</option>
                  </select>
                  {client.companyId === NEW_COMPANY && (
                    <div className="mt-3">
                      <label className="label">Nome da nova empresa *</label>
                      <input
                        className="input"
                        value={newCompanyName}
                        onChange={(e) => setNewCompanyName(e.target.value)}
                        placeholder="Ex.: Consentino Ltda"
                        required
                      />
                      <p className="text-dim text-xs mt-1">A empresa será cadastrada na base ao abrir o chamado.</p>
                    </div>
                  )}
                  {client.companyId !== NEW_COMPANY && (
                    <p className="text-dim text-xs mt-1">Se o e-mail ainda não estiver cadastrado, o cliente será criado nesta empresa.</p>
                  )}
                </div>
              </>
            )}
          </div>
        )}
        {catalog.length > 0 && (
          <div className="field">
            <label className="label">Catálogo de serviços (opcional)</label>
            <select className="select" defaultValue="" onChange={(e) => { applyCatalogItem(e.target.value); e.target.value = ''; }}>
              <option value="">Selecione um serviço para pré-preencher...</option>
              {catalog.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
        )}
        <div className="field">
          <label className="label">Assunto</label>
          <input className="input" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} required />
          {suggestions.length > 0 && (
            <div className="mt-2" style={{ background: 'var(--info-soft)', borderRadius: 'var(--radius-sm)', padding: '10px 12px' }}>
              <div className="text-sm font-semibold" style={{ marginBottom: 6 }}>💡 Talvez estes artigos resolvam seu problema:</div>
              <div className="flex flex-col gap-1">
                {suggestions.map((s) => (
                  <Link key={s.id} to={`/kb`} className="text-sm" style={{ color: 'var(--brand)' }}>📄 {s.title}</Link>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="field">
          <label className="label">Descrição</label>
          <RichEditor value={form.body} onChange={(html) => setForm({ ...form, body: html })} placeholder="Descreva o problema. Cole ou arraste imagens para ilustrar." minHeight={160} />
        </div>
        <div className="flex gap-4">
          <div className="field flex-1">
            <label className="label">Departamento</label>
            <select className="select" value={form.departmentId ?? ''} onChange={(e) => setForm({ ...form, departmentId: e.target.value || undefined })}>
              <option value="">Selecione</option>
              {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          </div>
          {/* Categoria: staff sempre vê o campo (com opção de criar na hora);
              cliente só vê quando há categorias disponíveis. */}
          {(isStaff || categoryOptions.length > 0) && (
            <div className="field flex-1">
              <label className="label">Categoria</label>
              <select className="select" value={form.categoryId ?? ''} onChange={(e) => setForm({ ...form, categoryId: e.target.value || undefined })}>
                <option value="">Selecione</option>
                {categoryOptions.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
                {isStaff && <option value={NEW_CATEGORY}>➕ Criar nova categoria...</option>}
              </select>
              {form.categoryId === NEW_CATEGORY && (
                <div className="mt-2">
                  <input
                    className="input"
                    value={newCategoryName}
                    onChange={(e) => setNewCategoryName(e.target.value)}
                    placeholder="Nome da nova categoria"
                    required
                  />
                  <p className="text-dim text-xs mt-1">A categoria será criada ao abrir o chamado.</p>
                </div>
              )}
            </div>
          )}
        </div>
        <div className="flex gap-4">
          <div className="field flex-1">
            <label className="label">Impacto</label>
            <select className="select" value={form.impact ?? ''} onChange={(e) => setForm({ ...form, impact: (e.target.value || undefined) as Impact | undefined })}>
              <option value="">Selecione</option>
              <option value={Impact.LOW}>{ImpactLabels.low}</option>
              <option value={Impact.MEDIUM}>{ImpactLabels.medium}</option>
              <option value={Impact.HIGH}>{ImpactLabels.high}</option>
            </select>
          </div>
          <div className="field flex-1">
            <label className="label">Urgência</label>
            <select className="select" value={form.urgency ?? ''} onChange={(e) => setForm({ ...form, urgency: (e.target.value || undefined) as Urgency | undefined })}>
              <option value="">Selecione</option>
              <option value={Urgency.LOW}>{UrgencyLabels.low}</option>
              <option value={Urgency.MEDIUM}>{UrgencyLabels.medium}</option>
              <option value={Urgency.HIGH}>{UrgencyLabels.high}</option>
            </select>
          </div>
        </div>
        {matrixPriority ? (
          <div className="field">
            <label className="label">Prioridade calculada</label>
            <span className={`badge badge-${matrixPriority}`}>{PriorityLabels[matrixPriority]}</span>
            <p className="text-sm text-muted mt-1">Definida automaticamente pela matriz impacto × urgência.</p>
          </div>
        ) : (
          <div className="field">
            <label className="label">Prioridade</label>
            <select className="select" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value as TicketPriority })}>
              <option value={TicketPriority.LOW}>Baixa</option>
              <option value={TicketPriority.NORMAL}>Normal</option>
              <option value={TicketPriority.HIGH}>Alta</option>
              <option value={TicketPriority.URGENT}>Urgente</option>
            </select>
            <p className="text-sm text-muted mt-1">Preencha impacto e urgência para calcular automaticamente.</p>
          </div>
        )}
        <div className="field">
          <label className="label">Agendar para (opcional)</label>
          <input className="input" type="datetime-local" value={scheduled} onChange={(e) => setScheduled(e.target.value)} />
        </div>
        <label className="flex items-center gap-2 field" style={{ cursor: 'pointer' }}>
          <input type="checkbox" checked={isTask} onChange={(e) => setIsTask(e.target.checked)} />
          Abrir como <strong>tarefa</strong> (o responsável recebe alerta por e-mail/Discord)
        </label>

        {isStaff && (
          <div className="field">
            <label className="label">Checklist (opcional)</label>
            <div className="text-dim text-xs" style={{ marginBottom: 'var(--s2)' }}>
              Crie categorias e, dentro delas, os subitens a cumprir no atendimento.
            </div>
            <div className="flex flex-col gap-3">
              {checklist.map((g, gi) => (
                <div key={gi} style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', padding: 'var(--s3)' }}>
                  <div className="flex gap-2 items-center">
                    <input
                      className="input"
                      style={{ fontWeight: 600 }}
                      value={g.text}
                      onChange={(e) => setChecklist((cl) => cl.map((x, i) => i === gi ? { ...x, text: e.target.value } : x))}
                      placeholder="Categoria (ex.: Preparação)"
                    />
                    <button type="button" className="btn btn-sm btn-ghost" style={{ color: 'var(--danger)' }} onClick={() => setChecklist((cl) => cl.filter((_, i) => i !== gi))} title="Remover categoria">✕</button>
                  </div>
                  <div className="flex flex-col gap-1" style={{ marginLeft: 14, marginTop: 8, borderLeft: '2px solid var(--border)', paddingLeft: 10 }}>
                    {g.children.map((c, ci) => (
                      <div key={ci} className="flex gap-2 items-center">
                        <input
                          className="input"
                          style={{ fontSize: 13, padding: '7px 10px' }}
                          value={c}
                          onChange={(e) => setChecklist((cl) => cl.map((x, i) => i === gi ? { ...x, children: x.children.map((y, j) => j === ci ? e.target.value : y) } : x))}
                          placeholder="Subitem"
                        />
                        <button type="button" className="btn btn-sm btn-ghost" onClick={() => setChecklist((cl) => cl.map((x, i) => i === gi ? { ...x, children: x.children.filter((_, j) => j !== ci) } : x))}>✕</button>
                      </div>
                    ))}
                    <div className="flex gap-2">
                      <input
                        className="input"
                        style={{ fontSize: 13, padding: '7px 10px' }}
                        value={newChild[gi] ?? ''}
                        onChange={(e) => setNewChild((s) => ({ ...s, [gi]: e.target.value }))}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); const t = (newChild[gi] ?? '').trim(); if (t) { setChecklist((cl) => cl.map((x, i) => i === gi ? { ...x, children: [...x.children, t] } : x)); setNewChild((s) => ({ ...s, [gi]: '' })); } } }}
                        placeholder="+ subitem"
                      />
                      <button
                        type="button"
                        className="btn btn-sm btn-ghost"
                        onClick={() => { const t = (newChild[gi] ?? '').trim(); if (t) { setChecklist((cl) => cl.map((x, i) => i === gi ? { ...x, children: [...x.children, t] } : x)); setNewChild((s) => ({ ...s, [gi]: '' })); } }}
                        disabled={!(newChild[gi] ?? '').trim()}
                      >+</button>
                    </div>
                  </div>
                </div>
              ))}
              <div className="flex gap-2">
                <input
                  className="input"
                  value={newGroup}
                  onChange={(e) => setNewGroup(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); if (newGroup.trim()) { setChecklist((cl) => [...cl, { text: newGroup.trim(), children: [] }]); setNewGroup(''); } } }}
                  placeholder="Nova categoria de checklist..."
                />
                <button type="button" className="btn btn-sm" onClick={() => { if (newGroup.trim()) { setChecklist((cl) => [...cl, { text: newGroup.trim(), children: [] }]); setNewGroup(''); } }} disabled={!newGroup.trim()}>+ Categoria</button>
              </div>
            </div>
          </div>
        )}

        <div className="field">
          <label className="label">Anexos (opcional)</label>
          <input ref={fileRef} type="file" multiple hidden onChange={(e) => onPickFiles(e.target.files)} />
          <button type="button" className="btn btn-sm" onClick={() => fileRef.current?.click()}>📎 Adicionar arquivos</button>
          {attachments.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-2">
              {attachments.map((a) => (
                <span key={a.id} className="badge badge-normal">
                  📎 {a.fileName}
                  <span style={{ cursor: 'pointer' }} onClick={() => setAttachments((l) => l.filter((x) => x.id !== a.id))}> ✕</span>
                </span>
              ))}
            </div>
          )}
        </div>
        <button className="btn btn-primary btn-lg" type="submit" disabled={saving}>
          {saving ? 'Salvando...' : 'Abrir chamado'}
        </button>
      </form>
    </div>
  );
}
